import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import authService from "../services/authService";
import menuService from "../services/menuService";
import { buildPath, hasPermission as hasMenuPermission } from "../utils/menuRouting";
import {
  clearActivity,
  isInactivityExceeded,
  markActivity,
  startInactivityMonitor,
  stopInactivityMonitor,
} from "../utils/sessionActivity";

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [myMenus, setMyMenus] = useState([]);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isMenusLoading, setIsMenusLoading] = useState(false);

  useEffect(() => {
    const session = authService.getSession();

    if (session) {
      setUser(session.user);
      setAccessToken(session.accessToken);
      setRefreshToken(session.refreshToken);
      setRoles(session.roles || []);
      setPermissions(session.permissions || []);
    }

    setIsInitializing(false);
  }, []);

  const login = useCallback(async (credentials) => {
    const session = await authService.login(credentials);
    setUser(session.user);
    setAccessToken(session.accessToken);
    setRefreshToken(session.refreshToken);
    setRoles(session.roles || []);
    setPermissions(session.permissions || []);
    markActivity();
    return session;
  }, []);

  const loadMyMenus = useCallback(async () => {
    setIsMenusLoading(true);
    try {
      const response = await menuService.getMyMenus();
      let list = response?.data || response || [];
      if (!Array.isArray(list)) list = [];

      // Filter out specific menus from sidebar (e.g. BL Expenses)
      const filterHiddenMenus = (nodes) => {
        return nodes
          .filter((n) => n.menuName !== "BL Expenses")
          .map((n) => ({
            ...n,
            children: Array.isArray(n.children) ? filterHiddenMenus(n.children) : [],
          }));
      };

      list = filterHiddenMenus(list);

      // Ensure Expense Category is visible under Settings Master if backend hasn't added it yet
      const hasExpenseCategory = (nodes) => {
        return (
          nodes.some(
            (n) =>
              (n.controllerName === "expense-category" ||
                n.controllerName === "settings-master") &&
              (n.actionName === "expense-category" ||
                n.actionName === "expense-categories" ||
                n.menuName === "Expense Category")
          ) ||
          nodes.some((n) => Array.isArray(n.children) && hasExpenseCategory(n.children))
        );
      };

      if (!hasExpenseCategory(list)) {
        const settingsMaster = list.find(
          (m) =>
            m.menuName === "Settings Master" ||
            m.controllerName === "settings-master" ||
            m.controllerName === "master"
        );
        if (settingsMaster) {
          if (!Array.isArray(settingsMaster.children)) settingsMaster.children = [];
          settingsMaster.children.push({
            id: "temp-expense-category-id",
            menuName: "Expense Category",
            controllerName: "settings-master",
            actionName: "expense-category",
            iconClass: "fas fa-tags",
            displayOrder: 10,
            permissions: { isRead: true, isCreate: true, isUpdate: true, isDelete: true },
          });
        }
      }

      // Ensure MIS Reports is visible in the menu tree directly after Report
      const hasMISReports = (nodes) => {
        return (
          nodes.some(
            (n) =>
              n.menuName === "MIS Reports" ||
              n.actionName === "/mis-reports" ||
              n.controllerName === "mis-reports" ||
              n.controllerName === "MISReport"
          ) ||
          nodes.some((n) => Array.isArray(n.children) && hasMISReports(n.children))
        );
      };

      if (!hasMISReports(list)) {
        const reportMenu = list.find(
          (m) =>
            m.menuName === "Report" ||
            m.controllerName === "Report" ||
            m.controllerName === "report-section" ||
            m.controllerName === "reports"
        );
        const reportOrder = reportMenu?.displayOrder ?? 4;

        list.push({
          id: "temp-mis-reports-id",
          menuName: "MIS Reports",
          controllerName: "mis-reports",
          actionName: "/mis-reports",
          iconClass: "fa-solid fa-chart-pie",
          displayOrder: reportOrder + 0.5,
          permissions: { isRead: true, isCreate: false, isUpdate: false, isDelete: false },
        });
      }

      setMyMenus(list);
      return list;
    } catch (_error) {
      setMyMenus([]);
      return [];
    } finally {
      setIsMenusLoading(false);
    }
  }, []);

  useEffect(() => {
    if (accessToken) {
      loadMyMenus();
    } else {
      setMyMenus([]);
      setIsMenusLoading(false);
    }
  }, [accessToken, loadMyMenus]);

  const hasRole = useCallback((roleName) => {
    const roleNames = roles.map((role) =>
      typeof role === "string" ? role : role?.roleName || role?.name
    );
    return roleNames.includes(roleName);
  }, [roles]);

  const hasPermission = useCallback((menuKey, action) => {
    const keyMap = {
      create: "isCreate",
      read: "isRead",
      update: "isUpdate",
      delete: "isDelete",
    };

    const permissionKey = keyMap[action?.toLowerCase()] || action;
    const matched = permissions.find((item) => {
      const menuName = item?.menuName || item?.menu?.menuName || item?.controllerName;
      return menuName === menuKey;
    });
    return Boolean(matched?.[permissionKey]);
  }, [permissions]);

  const findMenuByPath = useCallback((pathName) => {
    const search = (menuItems = []) => {
      for (const menu of menuItems) {
        const menuPath = buildPath(menu?.controllerName, menu?.actionName);
        if (menuPath === pathName) return menu;
        const children = Array.isArray(menu?.children) ? menu.children : [];
        const childMatch = search(children);
        if (childMatch) return childMatch;
      }
      return null;
    };
    return search(myMenus);
  }, [myMenus]);

  const canAccessPath = useCallback((pathName, action = "read") => {
    const menu = findMenuByPath(pathName);
    if (!menu) return false;
    return hasMenuPermission(menu, action);
  }, [findMenuByPath]);

  const hydrateSession = (session) => {
    setUser(session.user);
    setAccessToken(session.accessToken);
    setRefreshToken(session.refreshToken);
    setRoles(session.roles || []);
    setPermissions(session.permissions || []);
    markActivity();
    return session;
  };

  const register = useCallback(async (payload) => {
    const session = await authService.register(payload);
    return hydrateSession(session);
  }, []);

  const logout = useCallback(() => {
    authService.logout();
    stopInactivityMonitor();
    clearActivity();
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    setRoles([]);
    setPermissions([]);
    setMyMenus([]);
  }, []);

  useEffect(() => {
    if (!accessToken || !user) {
      stopInactivityMonitor();
      return;
    }

    const handleResumeOrVisibility = () => {
      if (document.visibilityState === "hidden") {
        return;
      }
      if (isInactivityExceeded()) {
        logout();
        return;
      }
      markActivity();
      startInactivityMonitor(logout);
    };

    const handleActivity = () => {
      markActivity();
      startInactivityMonitor(logout);
    };

    const events = [
      "click",
      "mousemove",
      "pointerdown",
      "pointermove",
      "keydown",
      "input",
      "change",
      "scroll",
      "touchstart",
      "touchmove",
    ];

    events.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });
    window.addEventListener("focus", handleResumeOrVisibility);
    document.addEventListener("visibilitychange", handleResumeOrVisibility);
    window.addEventListener("pageshow", handleResumeOrVisibility);

    handleResumeOrVisibility();

    return () => {
      events.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
      window.removeEventListener("focus", handleResumeOrVisibility);
      document.removeEventListener("visibilitychange", handleResumeOrVisibility);
      window.removeEventListener("pageshow", handleResumeOrVisibility);
      stopInactivityMonitor();
    };
  }, [accessToken, user, logout]);

  const value = useMemo(
    () => ({
      user,
      accessToken,
      refreshToken,
      roles,
      permissions,
      myMenus,
      login,
      register,
      logout,
      hasRole,
      hasPermission,
      findMenuByPath,
      canAccessPath,
      loadMyMenus,
      isInitializing,
      isMenusLoading,
      isAuthenticated: Boolean(accessToken && user),
    }),
    [
      isInitializing,
      accessToken,
      refreshToken,
      user,
      roles,
      permissions,
      myMenus,
      login,
      register,
      logout,
      hasRole,
      hasPermission,
      findMenuByPath,
      canAccessPath,
      loadMyMenus,
      isMenusLoading,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
